import { restore, kStructuredCloneMarker, kSendMarker } from '@cian/core/threads/index.js';
import { None, Some } from '@cian/core/option/index.js';
import { iteratorMap, iteratorFlatMap, Ordering, Ord } from '@cian/core/iterator/index.js';
import { priorityComparator } from '../comparator/comparator.js';

const NAMESPACE = 'config.source.storage';
const SOURCE_MARKER = NAMESPACE + '.Storage';
const SOURCE_SETTING_MARKER = NAMESPACE + '.StorageSetting';
class Storage {
    [kStructuredCloneMarker] = SOURCE_MARKER;
    storage = new Map();
    comparator;
    constructor(settings, comparator = priorityComparator) {
        if (settings) {
            if (Array.isArray(settings)) {
                this.batchAdd(settings);
            }
            else {
                this.storage = settings;
            }
        }
        this.comparator = comparator;
    }
    add(setting) {
        let settings = this.storage.get(setting.key);
        if (!settings) {
            settings = new StorageSetting([setting], this.comparator);
            this.storage.set(setting.key, settings);
            return;
        }
        settings.add(setting);
    }
    batchAdd(settings) {
        for (const setting of settings) {
            this.add(setting);
        }
    }
    select(key, filter) {
        const settings = this.storage.get(key);
        if (!settings) {
            return None();
        }
        return settings.get(filter);
    }
    selectAll(key, filter) {
        const settings = this.storage.get(key);
        if (!settings) {
            return None();
        }
        const settingsDump = filter ? settings.getAll().filter(filter) : settings.getAll();
        return settingsDump.length !== 0 ? Some(settingsDump) : None();
    }
    dump(filter) {
        return iteratorFlatMap(this.storage.values(), settings => filter ? settings.getAll().filter(filter) : settings.getAll());
    }
    removeByKey(setting) {
        this.storage.get(setting.key)?.removeByPriority(setting);
    }
    removeByKeys(settingKeys) {
        for (const setting of settingKeys) {
            this.removeByKey(setting);
        }
    }
    removeByFilter(filter, key) {
        if (key) {
            return this.storage.get(key)?.removeBy(filter);
        }
        for (const setting of this.storage.values()) {
            setting.removeBy(filter);
        }
    }
    clear() {
        this.storage.clear();
    }
}
class SendableStorage extends Storage {
    static restore() {
        return {
            [kStructuredCloneMarker]: SOURCE_MARKER,
            from(serialized) {
                const storage = new Map();
                const comparator = restore(serialized.comparator).unwrap();
                for (const [key, settings] of serialized.settings) {
                    storage.set(key, new StorageSetting(settings[1], comparator));
                }
                return new SendableStorage(storage, comparator);
            },
        };
    }
    [kSendMarker] = 1;
    [kStructuredCloneMarker] = SOURCE_MARKER;
    constructor(settings, comparator = priorityComparator) {
        super(settings, comparator);
    }
    toStructuredClone() {
        return [
            this[kStructuredCloneMarker],
            {
                settings: Array.from(iteratorMap(this.storage, ([key, storage]) => [
                    key,
                    storage.toStructuredClone(),
                ])),
                comparator: this.comparator.toStructuredClone(),
            },
        ];
    }
}
class StorageSetting {
    [kStructuredCloneMarker] = SOURCE_SETTING_MARKER;
    key;
    // Настройки "лениво" сортируются по `priority` в обратном порядке
    settings;
    comparator;
    isSorted;
    constructor(settings, comparator = priorityComparator) {
        if (Array.isArray(settings)) {
            this.key = settings[0].key;
            this.settings = settings;
            this.isSorted = false;
        }
        else {
            this.key = settings.key;
            this.settings = settings.settings;
            this.isSorted = settings.isSorted;
        }
        this.comparator = comparator;
    }
    get(filter) {
        let setting;
        if (!filter) {
            setting = this.getSorted()[0];
        }
        else {
            setting = this.getSorted().find(filter);
        }
        return setting ? Some(setting) : None();
    }
    getAll() {
        return this.settings;
    }
    getSorted() {
        if (!this.isSorted) {
            this.settings.sort(settingSortByPriority);
            this.isSorted = true;
        }
        return this.settings;
    }
    add(setting) {
        const current = this.settings.find(s => this.comparator(s, setting));
        if (current) {
            // UNSAFE: Выполняется мутация существующего объекта в памяти, если доступ к настройке получен
            // напрямую из `ISource`, то это может привести к неожиданным результатам
            current.value = setting.value;
            current.filter = setting.filter;
            return;
        }
        if (this.isSorted) {
            // Если приоритет одинаковый, то новая настройка будет старше
            const index = this.settings.findIndex(s => settingSortByPriority(s, setting) > Ordering.Less);
            if (index >= 0) {
                this.settings.splice(index, 0, setting);
            }
            else {
                this.settings.push(setting);
            }
        }
        else {
            this.settings.push(setting);
        }
    }
    /**
     * Удаляет все настройки с указанным приоритетом
     *
     * ```js
     * const storage = new StorageSetting([
     *  { key: 'key', value: 'value', priority: 1 },
     *  { key: 'key', value: 'value', priority: 2 },
     *  { key: 'key', value: 'value', priority: 1, filter: { 'session-id': '1' } },
     * ]);
     *
     * storage.removeByPriority({ priority: 1 });
     *
     * assert.deepEqual(storage.getAll(), [
     *  { key: 'key', value: 'value', priority: 2 },
     * ]);
     * ```
     *
     * @param {(SettingKey | ISetting)} setting
     * @memberof StorageSetting
     */
    removeByPriority(setting) {
        this.settings = this.settings.filter(s => s.priority !== setting.priority);
    }
    /**
     * Удаляет все настройки, удовлетворяющие переданному фильтру
     *
     * @param {SettingFilterFn} filter
     * @memberof StorageSetting
     */
    removeBy(filter) {
        this.settings = this.settings.filter(s => !filter(s));
    }
    /**
     * Удаляет настройку из хранилища, поиск осуществляется по компаратору {@link SettingComparator}
     *
     * @param {ISetting} setting
     * @memberof StorageSetting
     */
    removeBySetting(setting) {
        this.settings = this.settings.filter(s => !this.comparator(s, setting));
    }
    toStructuredClone() {
        return [
            this[kStructuredCloneMarker],
            {
                settings: this.settings,
                key: this.key,
                isSorted: this.isSorted,
            },
        ];
    }
}
/**
 * Сортировка настроек по приоритету. Настройки сортируются по убыванию приоритета. Если предполагается,
 * что в коллекции настроек могут быть настройки с одинаковым приоритетом, то старшей настройкой считается
 * та, что была добавлена последней
 *
 * @param {ISetting} a
 * @param {ISetting} b
 * @returns {Ordering}
 */
function settingSortByPriority(a, b) {
    const result = Ord.numberToOrd(b.priority, a.priority);
    return result === Ordering.Equal ? Ordering.Less : result;
}

export { SendableStorage, Storage, StorageSetting, settingSortByPriority };
//# sourceMappingURL=storage.js.map
